import { inject, Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';

// Import POS service API
import { PosControllerService } from '@app/api/pos-service/api/posController.service';
import { PosReqDTO } from '@app/api/pos-service/model/posReqDTO';
import { PosResDTO } from '@app/api/pos-service/model/posResDTO';
import { PosPageReqDTO } from '@app/api/pos-service/model/posPageReqDTO';
import { PageInfoPosResDTO } from '@app/api/pos-service/model/pageInfoPosResDTO';

// Import channel-service API for shop data
import { PhysicalShopControllerService } from '@app/api/channel-service/api/physicalShopController.service';
import { PhysicalShopShowDTO } from '@app/api/channel-service/model/physicalShopShowDTO';
import { PageInfoPhysicalShopVO } from '@app/api/channel-service/model/pageInfoPhysicalShopVO';
import { PhysicalShopVO } from '@app/api/channel-service/model/physicalShopVO';

import { PhysicalShopDetailVO } from '@app/api/channel-service/model/physicalShopDetailVO';
import { TranslateService } from '@ngx-translate/core';
import { BackendValidationErrorConfig, handleBackendValidationError } from '@app/shared/utils/error-handler.util';

// Local interfaces - Updated to match POS service API
export interface PosProfile {
  id: number;
  code: string;
  status: boolean; // Converted from number to boolean for UI
  description?: string;
  channelShopId: number;
  channelShopName: string;
  channelShopCode: string;
  channelShopAddress: string;
  channelShopType?: string;
  deviceId?: string;
  deleteFlag?: boolean;
  activatedAt?: string;
  creationDate: string;
  lastUpdateDate?: string;
  createdBy?: string;
  lastUpdatedBy?: string;
}

export interface PosProfileListResponse {
  data: PosProfile[];
  totalRecords: number;
  loading: boolean;
}

export interface PosProfileSearchParams {
  code?: string;
  channelShopId?: number;
  channelShopCode?: string;
  status?: number; // 0 - Inactive, 1 - Active
  page?: number;
  size?: number;
}

export interface ShopOption {
  label: string;
  value: number;
}

@Injectable({
  providedIn: 'root'
})
export class PosProfileService {
  private translateService = inject(TranslateService);

  /** Page size for `physical/shop/page` when aggregating all shops */
  private readonly physicalShopPageSize = 200;

  constructor(
    private posService: PosControllerService,
    private physicalShopService: PhysicalShopControllerService
  ) {}

  /**
   * Process API response that might be a Blob or JSON
   */
  private processApiResponse<T>(response: any): Observable<T> {
    if (response instanceof Blob) {
      return from(
        new Promise<T>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const textData = reader.result as string;
              
              // Try to parse as JSON first
              try {
                const jsonData = JSON.parse(textData);
                // Check if the response contains an error
                if (this.isErrorResponse(jsonData)) {
                  reject(new Error(jsonData.message || 'Unknown error occurred'));
                } else {
                  resolve(jsonData as T);
                }
              } catch (jsonError) {
                // If JSON parsing fails, check if it's a plain text response
                if (textData.trim().length > 0) {
                  // For plain text responses, return a success object
                  resolve({
                    message: textData.trim(),
                    success: true,
                    isPlainText: true
                  } as T);
                } else {
                  // Empty response
                  resolve({
                    message: 'Operation completed successfully',
                    success: true,
                    isPlainText: true
                  } as T);
                }
              }
            } catch (error) {
              console.error('Error processing blob data:', error);
              reject(error);
            }
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsText(response);
        })
      );
    }
    
    // Check if the response contains an error
    if (this.isErrorResponse(response)) {
      throw new Error(response.message || 'Unknown error occurred');
    }
    
    return of(response as T);
  }

  // /**
  //  * Check if the response contains an error
  //  */
  private isErrorResponse(response: any): boolean {
    // Check for common error response patterns
    if (response && typeof response === 'object') {

      if (response.httpStatus && (response.httpStatus >= 400)) {
        return true;
      }
      
      // Pattern 2: {error: 'Error message'}
      if (response.error) {
        return true;
      }
      // Pattern 3: {status: 'error', message: 'Error message'}
      if (response.status === 'error') {
        return true;
      }
      // Pattern 4: {success: false} - explicit failure
      if (response.success === false) {
        return true;
      }
      // Pattern 5: {message: 'Error message'} with error-like content
      if (response.message && typeof response.message === 'string') {
        const message = response.message.toLowerCase();
        if (message.includes('error') || message.includes('failed') || message.includes('invalid')) {
          return true;
        }
      }
      
      // Pattern 6: Check for actual HTTP error response structure
      if (response.statusCode && response.statusCode >= 400) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Map loaded shops to dropdown options
   */
  private mapPhysicalShopsToOptions(
    shops: PhysicalShopVO[],
    dropEmptyNames: boolean
  ): ShopOption[] {
    const list = dropEmptyNames ? shops.filter((shop) => !!shop.name) : shops;
    return list.map((shop) => ({
      label: shop.name || '',
      value: shop.id || 0,
    }));
  }

  /**
   * Load every page from `channel/v1/physical/shop/page` and merge results.
   */
  private fetchAllPhysicalShopsAsOptions(
    base: Omit<PhysicalShopShowDTO, 'pageRequest'>,
    dropEmptyNames: boolean
  ): Observable<ShopOption[]> {
    const load = (
      page: number,
      accumulated: PhysicalShopVO[]
    ): Observable<ShopOption[]> => {
      const dto: PhysicalShopShowDTO = {
        ...base,
        pageRequest: { page, size: this.physicalShopPageSize },
      };
      return this.physicalShopService.showPage(dto, 'body', false).pipe(
        switchMap((response) =>
          this.processApiResponse<PageInfoPhysicalShopVO>(response)
        ),
        switchMap((resp) => {
          const batch = resp.pageContent || [];
          const merged = [...accumulated, ...batch];

          if (batch.length === 0) {
            return of(this.mapPhysicalShopsToOptions(merged, dropEmptyNames));
          }
          if (resp.hasNextPage === false) {
            return of(this.mapPhysicalShopsToOptions(merged, dropEmptyNames));
          }
          if (resp.hasNextPage === true) {
            return load(page + 1, merged);
          }
          const total = resp.totalElements;
          if (typeof total === 'number' && merged.length >= total) {
            return of(this.mapPhysicalShopsToOptions(merged, dropEmptyNames));
          }
          if (batch.length < this.physicalShopPageSize) {
            return of(this.mapPhysicalShopsToOptions(merged, dropEmptyNames));
          }
          return load(page + 1, merged);
        }),
        catchError((error) => {
          console.error('Error fetching physical shops (paged):', error);
          throw error;
        })
      );
    };

    return load(0, []);
  }

  /**
   * Get physical shops for search dropdown (all shops)
   */
  getPhysicalShopsForSearch(): Observable<ShopOption[]> {
    return this.fetchAllPhysicalShopsAsOptions(
      { status: undefined, deleteFlag: false },
      true
    );
  }

  getPhysicalShopsOptionsPage(params: {
    page: number;
    size: number;
    searchText: string;
  }): Observable<{
    options: ShopOption[];
    pageContentLength: number;
    totalElements?: number;
  }> {
    const dto: PhysicalShopShowDTO = {
      status: undefined,
      deleteFlag: false,
      name: params.searchText?.trim() || undefined,
      pageRequest: { page: params.page - 1, size: params.size },
    };

    return this.physicalShopService.showPage(dto, 'body', false).pipe(
      switchMap((response) =>
        this.processApiResponse<PageInfoPhysicalShopVO>(response),
      ),
      map((resp) => {
        const batch = resp.pageContent || [];
        const options = this.mapPhysicalShopsToOptions(batch, true);
        return {
          options,
          pageContentLength: options.length,
          totalElements: resp.totalElements,
        };
      }),
      catchError(() =>
        of({ options: [], pageContentLength: 0 }),
      ),
    );
  }

  /**
   * Get physical shops for form dropdown (active shops only)
   */
  getPhysicalShopsForForm(): Observable<ShopOption[]> {
    return this.fetchAllPhysicalShopsAsOptions(
      { status: true, deleteFlag: false },
      false
    );
  }

  /**
   * Get shop details by ID using showDetails API
   */
  getShopDetailsById(channelShopId: number): Observable<PhysicalShopDetailVO | null> {
    return this.physicalShopService.showDetails(channelShopId, 'body', false).pipe(
      switchMap((response) => this.processApiResponse<PhysicalShopDetailVO>(response)),
      map((response: PhysicalShopDetailVO) => {
        return response;
      }),
      catchError((error) => {
        console.error('Error fetching shop details:', error);
        throw error;
      })
    );
  }

  /**
   * Get POS profile terminals using POS service API
   */
  getPosProfile(params: PosProfileSearchParams = {}): Observable<PosProfileListResponse> {
    const pageRequest: PosPageReqDTO = {
      page: (params.page || 1) - 1, // Convert 1-based to 0-based for API
      size: params.size || 10,
      sortBy: 'creationDate',
      ascending: false,
      code: params.code,
      channelShopId: params.channelShopId,
      channelShopCode: params.channelShopCode,
      status: params.status
    };

    return this.posService.getAllPosPage(pageRequest, 'body', false, {
      httpHeaderAccept: 'application/json' as any
    }).pipe(
      switchMap((response) => this.processApiResponse<PageInfoPosResDTO>(response)),
      map((response: PageInfoPosResDTO) => {
        const posList = response.pageContent || [];
        const posProfiles: PosProfile[] = posList.map(pos => this.mapPosResDTOToPosProfile(pos));
        
        return {
          data: posProfiles,
          totalRecords: response.totalElements || 0,
          loading: false
        };
      }),
      catchError((error) => {
        console.error('Error fetching POS profiles:', error);
        throw error;
      })
    );
  }

  /**
   * Get POS profile terminal by ID using POS service API
   */
  getPosProfileById(id: number): Observable<PosProfile | null> {
    return this.posService.getPosById(id, 'body', false, {
      httpHeaderAccept: 'application/json' as any
    }).pipe(
      switchMap((response) => this.processApiResponse<PosResDTO>(response)),
      map((response: PosResDTO) => {
        return this.mapPosResDTOToPosProfile(response);
      }),
      catchError((error) => {
        console.error('Error fetching POS profile by ID:', error);
        throw error;
      })
    );
  }

  /**
   * Create new POS profile terminal using POS service API
   */
  createPosProfile(terminalData: Partial<PosProfile>): Observable<string> {
    const posReqDTO: PosReqDTO = {
      code: terminalData.code || '',
      channelShopId: terminalData.channelShopId || 0,
      description: terminalData.description,
      type: terminalData.channelShopType as PosReqDTO.TypeEnum,
      deviceId: terminalData.deviceId, 
    };

    return this.posService.createPos(posReqDTO, 'body', false, {
      httpHeaderAccept: 'application/json' as any
    }).pipe(
      switchMap((response) => this.processApiResponse(response)),
      map((response: any) => {
        // Handle plain text responses
        if (response.isPlainText) {
          return response.message;
        }
        // Handle JSON responses
        return 'POS profile terminal created successfully';
      }),
      catchError((error) => {
        console.error('Error creating POS profile:', error);
        throw error;
      })
    );
  }

  /**
   * Update POS profile terminal using POS service API
   */
  updatePosProfile(id: number, terminalData: Partial<PosProfile>): Observable<string> {
    const posReqDTO: PosReqDTO = {
      code: terminalData.code || '',
      channelShopId: terminalData.channelShopId || 0,
      description: terminalData.description,
      type: terminalData.channelShopType as PosReqDTO.TypeEnum,
      deviceId: terminalData.deviceId, 
    };

    return this.posService.updatePos(id, posReqDTO, 'body', false, {
      httpHeaderAccept: 'application/json' as any
    }).pipe(
      switchMap((response) => this.processApiResponse(response)),
      map((response: any) => {
        // Handle plain text responses
        if (response.isPlainText) {
          return response.message;
        }
        // Handle JSON responses
        return 'POS profile terminal updated successfully';
      }),
      catchError((error) => {
        console.error('Error updating POS profile:', error);
        throw error;
      })
    );
  }

  /**
   * Update POS profile terminal status using POS service API
   */
  updatePosProfileStatus(id: number, status: boolean): Observable<string> {
    const statusNumber = status ? 1 : 0; // Convert boolean to number (1 - Active, 0 - Inactive)

    return this.posService.updatePosStatus(id, statusNumber, 'body', false).pipe(
      switchMap((response) => this.processApiResponse(response)),
      map((response: any) => {
        // Handle plain text responses
        if (response.isPlainText) {
          return response.message;
        }
        // Handle JSON responses
        return `POS profile terminal ${status ? 'enabled' : 'disabled'} successfully`;
      }),
      catchError((error) => {
        console.error('Error updating POS profile status:', error);
        throw error;
      })
    );
  }

  /**
   * Delete POS profile terminal using POS service API
   */
  deletePosProfile(id: number): Observable<string> {
    return this.posService.deletePos(id, 'body', false).pipe(
      switchMap((response) => this.processApiResponse(response)),
      map((response: any) => {
        // Handle plain text responses
        if (response.isPlainText) {
          return response.message;
        }
        // Handle JSON responses
        return 'POS profile terminal deleted successfully';
      }),
      catchError((error) => {
        console.error('Error deleting POS profile:', error);
        throw error;
      })
    );
  }

  /**
   * Generate activation code for POS terminal
   */
  generateActivationCode(id: number): Observable<any> {
    return this.posService.generateActivationCode(id, 'body', false, {
      httpHeaderAccept: '*/*' as any
    }).pipe(
      switchMap((response) => this.processApiResponse(response)),
      map((response: any) => {
        // Response should contain activation code
        return response;
      }),
      catchError((error) => {
        console.error('Error generating activation code:', error);
        throw error;
      })
    );
  }

  /**
   * Map PosResDTO to PosProfile interface
   */
  private mapPosResDTOToPosProfile(posResDTO: PosResDTO): PosProfile {
    return {
      id: posResDTO.id || 0,
      code: posResDTO.code || '',
      status: posResDTO.status === 1, // Convert number to boolean
      description: posResDTO.description,
      channelShopId: posResDTO.channelShopId || 0,
      channelShopName: posResDTO.channelShopName || '',
      channelShopCode: posResDTO.channelShopCode || '',
      channelShopAddress: posResDTO.channelShopAddress || '',
      channelShopType: posResDTO.type,
      deviceId: posResDTO.deviceId,
      deleteFlag: posResDTO.deleteFlag,
      creationDate: posResDTO.creationDate || '',
      lastUpdateDate: posResDTO.lastUpdateDate,
      activatedAt: posResDTO.activatedAt || '',
      createdBy: posResDTO.createdBy,
      lastUpdatedBy: posResDTO.lastUpdatedBy
    };
  }

  handleBackendValidationError(error: any, operation: 'create' | 'update' | 'delete'): { severity: string; summary: string; detail: string } {
    const config: BackendValidationErrorConfig = {
      errorPatterns: [
        { patterns: ['Code already exists'], key: 'common.validation.code_exists' },
        { patterns: ['Existing products'], key: 'pos.profile.form.validation.pos_terminal_product_exists' },
        { patterns: ['deletion is not allowed'], key: 'pos.profile.form.validation.delete_pos_terminal_product_exists' }
      ],
      defaultErrorKeys: {
        create: 'pos.messages.create_error',
        update: 'pos.messages.update_error',
        delete: 'pos.messages.delete_error',
      }
    };

    return handleBackendValidationError(error, operation, config, this.translateService);
  }
}
